import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { format, startOfMonth } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { useModuleVisibility } from '../../modules/useModuleVisibility';
import { DashboardService } from '../../../services/dashboard.service';
import { AttendanceService } from '../../../services/attendance.service';
import { LeaveService } from '../../../services/leave.service';
import { TaskService } from '../../../services/task.service';
import { NoticeService } from '../../../services/notice.service';
import { AuthService } from '../../../services/auth.service';
import { UserDashboard } from '../../../interfaces/dashboard.interface';
import { Attendance } from '../../../interfaces/attendance.interface';
import { LeaveApplication, LeaveStatus } from '../../../interfaces/leave.interface';
import { ITask } from '../../../interfaces/task.interface';
import { INotice } from '../../../interfaces/notice.interface';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';
import { displayUsername } from '../../../utils/userUtils';
import StatTile from '../charts/StatTile';
import HBars from '../charts/HBars';
import Columns from '../charts/Columns';
import { DashSection, DashEmpty } from './DashSection';
import TeamToday from './TeamToday';
import { activeBalances, daysBetween, isApprovedOn, summarizeMonth } from './personalDashboardUtils';
import MyBreakdownsDashSection from '../../maintenance/MyBreakdownsDashSection';
import MyWidgetsSection from '../board/MyWidgetsSection';

const STATUS_COLOR: Record<LeaveStatus, 'warning' | 'success' | 'error' | 'default'> = {
    [LeaveStatus.Pending]: 'warning',
    [LeaveStatus.Approved]: 'success',
    [LeaveStatus.Rejected]: 'error',
    [LeaveStatus.Cancelled]: 'default',
};

const fmtTime = (iso: string) => format(new Date(iso), 'hh:mm a');
const fmtNum = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/**
 * Personal home for anyone without an admin/manager board: a KPI strip, charts and lists built
 * from the caller's own data, plus a "My team - today" block for department heads. Every source is
 * fetched independently so one failing endpoint only removes the widgets that depend on it.
 */
const PersonalDashboard: React.FC = () => {
    const { user } = useAuth();
    const { visibility, enabledModules } = useModuleVisibility();
    const canOpenApprovals = usePermission(PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS);
    const canSeeTeamAttendance = usePermission(PERMISSIONS.ATTENDANCE_VIEW_ALL);

    const [loading, setLoading] = useState(true);
    const [me, setMe] = useState<UserDashboard | null>(null);
    const [month, setMonth] = useState<Attendance[] | null>(null);
    const [apps, setApps] = useState<LeaveApplication[] | null>(null);
    const [tasks, setTasks] = useState<ITask[] | null>(null);
    const [notices, setNotices] = useState<INotice[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const institutionId = AuthService.getSelectedInstitution();
            const now = new Date();
            const [meR, monthR, appsR, tasksR, noticesR] = await Promise.allSettled([
                DashboardService.getMyDashboard(institutionId),
                AttendanceService.getLoggedInUserAttendanceList(startOfMonth(now), now),
                LeaveService.getLeaveApplications(0, 20),
                TaskService.myTasks(institutionId, 0, 20, ['OPEN', 'IN_PROGRESS']),
                NoticeService.feed(institutionId, 0, 5),
            ]);
            if (cancelled) return;
            if (meR.status === 'fulfilled') setMe(meR.value);
            if (monthR.status === 'fulfilled') setMonth(monthR.value.data.content);
            if (appsR.status === 'fulfilled') setApps(appsR.value.content);
            if (tasksR.status === 'fulfilled') setTasks(tasksR.value.content);
            if (noticesR.status === 'fulfilled') setNotices(noticesR.value.content);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    const today = toLocalIsoDate();

    const balances = useMemo(() => (me ? activeBalances(me.leaveBalances) : []), [me]);
    const summary = useMemo(() => (month ? summarizeMonth(month) : null), [month]);
    const nextHoliday = useMemo(() => {
        const list = (me?.upcomingHolidays ?? [])
            .filter(h => h.endDate >= today)
            .sort((a, b) => a.startDate.localeCompare(b.startDate));
        return list[0] ?? null;
    }, [me, today]);
    const holidays = useMemo(() => (me?.upcomingHolidays ?? [])
        .filter(h => h.endDate >= today).sort((a, b) => a.startDate.localeCompare(b.startDate)).slice(0, 5), [me, today]);
    const onLeaveToday = useMemo(() => !!apps?.some(a => isApprovedOn(a, today)), [apps, today]);
    const pendingCount = me?.myPendingLeaves ?? apps?.filter(a => a.leaveStatus === LeaveStatus.Pending).length ?? null;

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }
    if (!me && !month && !apps) {
        return <Alert severity="error" sx={{ mt: 2 }} data-help-page="dashboard.personal" data-help-section="dashboard.personal.loadError">Failed to load dashboard data</Alert>;
    }

    const can = (card: string, module?: 'attendance' | 'leave' | 'payroll') =>
        !!visibility[card] && (!module || enabledModules.has(module));
    const showLeave = can('leave', 'leave');
    const showAttendance = can('attendance', 'attendance');
    const showPayslips = enabledModules.has('payroll');

    const displayName = user?.name || displayUsername(user?.username) || 'there';
    const isHead = user?.isDepartmentAdmin === true;

    const todayRow = me?.todayAttendance ?? null;
    let statusValue = 'Not clocked in';
    let statusSub: string | undefined;
    let statusTone: 'default' | 'good' | 'warning' = 'warning';
    if (todayRow) {
        statusTone = 'good';
        // Clock-in stamps endTime = startTime; only a later out-punch means the day is closed.
        const clockedOut = !!todayRow.endTime && todayRow.endTime !== todayRow.startTime;
        statusValue = clockedOut ? 'Clocked out' : 'Clocked in';
        statusSub = `In ${fmtTime(todayRow.startTime)}${clockedOut && todayRow.endTime ? ` · Out ${fmtTime(todayRow.endTime)}` : ''}`;
    } else if (onLeaveToday) {
        statusValue = 'On leave';
        statusTone = 'default';
    }

    const leaveRemaining = balances.reduce((s, b) => s + b.remaining, 0);
    const leaveAllocated = balances.reduce((s, b) => s + b.allocated, 0);
    const nextHolidayDays = nextHoliday ? daysBetween(today, nextHoliday.startDate) : null;

    const openTasks = tasks ?? [];
    const recentApps = (apps ?? []).slice(0, 5);

    const personalActions = (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {showLeave && <Button data-help="dashboard.personal.applyLeave" component={RouterLink} to="/leave-applications" size="small" variant={isHead ? 'outlined' : 'contained'}>Apply for leave</Button>}
            {showAttendance && <Button data-help="dashboard.personal.myAttendance" component={RouterLink} to="/attendance" size="small" variant="outlined">My attendance</Button>}
            {showPayslips && <Button data-help="dashboard.personal.myPayslips" component={RouterLink} to="/my-payslips" size="small" variant="outlined">My payslips</Button>}
        </Stack>
    );

    return (
        <Box sx={{ p: { xs: 0, md: 1 } }} data-help-page="dashboard.personal">
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                <Box data-help-section="dashboard.personal.header">
                    <Typography variant="h5" fontWeight="bold">Hello, {displayName}</Typography>
                    <Typography variant="body2" color="text.secondary">{format(new Date(), 'EEEE, dd MMM yyyy')}</Typography>
                </Box>
                {/* A department head's header leads to team work (approvals, team attendance); their
                    own self-service actions sit with the "Me" section below. Operational employees
                    keep the personal actions up here. */}
                {isHead
                    ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {canOpenApprovals && <Button data-help="dashboard.personal.leaveApprovals" component={RouterLink} to="/all-leave-applications" size="small" variant="contained">Leave approvals</Button>}
                            {canSeeTeamAttendance && <Button data-help="dashboard.personal.teamToday" component={RouterLink} to="/attendance/today" size="small" variant="outlined">Team attendance today</Button>}
                            {canSeeTeamAttendance && <Button data-help="dashboard.personal.teamRecords" component={RouterLink} to="/attendance/employee" size="small" variant="outlined">Team attendance records</Button>}
                        </Stack>
                    )
                    : personalActions}
            </Stack>

            {isHead && (
                <TeamToday
                    username={user?.username}
                    approvalsPath={canOpenApprovals ? '/all-leave-applications' : '/leave-applications'}
                />
            )}

            {isHead && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" data-help-section="dashboard.personal.meRow">Me</Typography>
                    {personalActions}
                </Stack>
            )}
            <Paper variant="outlined" sx={{ p: 1, mb: 2 }} data-help-section="dashboard.personal.kpis">
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {me && <StatTile data-help="dashboard.personal.tile.today" label="Today" value={statusValue} sub={statusSub} tone={statusTone} />}
                    {summary && <StatTile data-help="dashboard.personal.tile.daysPresent" label="Days present" value={summary.daysPresent} sub="This month" />}
                    {summary && (
                        <StatTile data-help="dashboard.personal.tile.lateDays" label="Late days" value={summary.lateDays} sub="This month" tone={summary.lateDays > 0 ? 'warning' : 'good'} />
                    )}
                    {me && balances.length > 0 && (
                        <StatTile data-help="dashboard.personal.tile.leaveLeft" label="Leave left" value={fmtNum(leaveRemaining)} sub={`of ${fmtNum(leaveAllocated)} days`} />
                    )}
                    {pendingCount != null && (
                        <StatTile data-help="dashboard.personal.tile.pendingLeave" label="Pending leave" value={pendingCount} tone={pendingCount > 0 ? 'warning' : 'default'} sub="Awaiting approval" />
                    )}
                    {me && (
                        <StatTile data-help="dashboard.personal.tile.nextHoliday"
                            label="Next holiday" value={nextHoliday ? nextHoliday.name : 'None'}
                            sub={nextHoliday && nextHolidayDays != null
                                ? `${formatDate(nextHoliday.startDate)} · ${nextHolidayDays <= 0 ? 'today' : `in ${nextHolidayDays} day${nextHolidayDays === 1 ? '' : 's'}`}`
                                : 'No upcoming holiday'}
                        />
                    )}
                </Stack>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2, mb: 2 }}>
                {summary && (
                    <DashSection data-help-section="dashboard.personal.hours" title="My hours this month" subtitle="Hours on site per day with a completed punch">
                        {summary.hoursPerDay.length === 0 ? <DashEmpty text="No completed days yet this month." />
                            : <Columns data={summary.hoursPerDay} yFmt={n => `${fmtNum(n)}h`} tickFmt={n => `${fmtNum(n)}h`} />}
                    </DashSection>
                )}
                {me && balances.length > 0 && (
                    <DashSection data-help-section="dashboard.personal.leaveBalance" title="Leave balance" subtitle="Days remaining by type">
                        <HBars
                            data={balances.map(b => ({
                                label: b.leaveType, value: b.remaining,
                                tooltip: [
                                    { label: 'Remaining', value: fmtNum(b.remaining) },
                                    { label: 'Used', value: fmtNum(b.availed) },
                                    { label: 'Allocated', value: fmtNum(b.allocated) },
                                ],
                            }))}
                            vFmt={fmtNum}
                        />
                    </DashSection>
                )}
                {me && (
                    <DashSection data-help-section="dashboard.personal.holidays" title="Upcoming holidays">
                        {holidays.length === 0 ? <DashEmpty text="No upcoming holidays." /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} data-testid="holiday-list">
                                {holidays.map(h => (
                                    <Stack key={h.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.name}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(h.startDate)}{h.endDate !== h.startDate ? ` – ${formatDate(h.endDate)}` : ''}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </DashSection>
                )}
                {apps && showLeave && (
                    <DashSection data-help-section="dashboard.personal.recentLeave" title="My recent leave" subtitle="Latest applications">
                        {recentApps.length === 0 ? <DashEmpty text="No leave applications yet." /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} data-testid="recent-leave">
                                {recentApps.map(a => (
                                    <Stack key={a.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Link component={RouterLink} to="/leave-applications" underline="hover" sx={{ fontWeight: 600 }}>{a.leaveType}</Link>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(a.startDate)} – {formatDate(a.endDate)}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Chip size="small" label={a.leaveStatus} color={STATUS_COLOR[a.leaveStatus] ?? 'default'} />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </DashSection>
                )}
                {tasks && openTasks.length > 0 && (
                    <DashSection data-help-section="dashboard.personal.tasks" title="My open tasks" subtitle="Assigned to me">
                        <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} data-testid="my-tasks">
                            {openTasks.slice(0, 5).map(t => (
                                <Stack key={t.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Link component={RouterLink} to="/tasks" underline="hover" sx={{ fontWeight: 600 }}>{t.title}</Link>
                                    <Box sx={{ flex: 1 }} />
                                    {t.dueDate && <Typography variant="body2" color="text.secondary">Due {formatDate(t.dueDate)}</Typography>}
                                    {t.isOverdue && <Chip size="small" color="error" label="Overdue" />}
                                </Stack>
                            ))}
                        </Stack>
                    </DashSection>
                )}
                {notices && notices.length > 0 && (
                    <DashSection data-help-section="dashboard.personal.notices" title="Recent notices">
                        <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} data-testid="my-notices">
                            {notices.slice(0, 4).map(n => (
                                <Stack key={n.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Link component={RouterLink} to="/notices" underline="hover" sx={{ fontWeight: n.hasCurrentUserRead ? 400 : 600 }}>{n.title}</Link>
                                    <Box sx={{ flex: 1 }} />
                                    <Typography variant="body2" color="text.secondary">{formatDate(n.publishAt)}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </DashSection>
                )}
            </Box>
            <MyBreakdownsDashSection />
            <MyWidgetsSection boardKey="personal" />
        </Box>
    );
};

export default PersonalDashboard;
